import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import {
  api,
  setApiBase,
  getConfiguredApiBase,
  setGoogleApiKey,
  hasGoogleApiKey,
  setVeo3ApiKeys,
  hasVeo3ApiKeys,
  setMusicGptApiKey,
  hasMusicGptApiKey,
  setPromoCode,
  hasPromoCode,
  hasFalApiKey,
  getVideoSimulatorMode,
  setVideoSimulatorMode,
} from '../../api/client'
import {
  setSupabaseUrl,
  setSupabaseAnonKey,
  getConfiguredSupabaseUrl,
  getConfiguredSupabaseAnonKey,
  hasSupabaseConfig,
  reinitializeSupabase,
} from '../../utils/supabase'

interface SettingsFormProps {
  onSave?: () => void
  compact?: boolean
}

export default function SettingsForm({ onSave, compact = false }: SettingsFormProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { session } = useAuth()
  const [apiUrl, setApiUrl] = useState('')
  const [tokenCopied, setTokenCopied] = useState(false)
  const [apiKeyInput, setApiKeyInput] = useState('')
  const [apiKeySet, setApiKeySet] = useState(false)
  const [apiKeyMessage, setApiKeyMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Supabase config
  const [supabaseUrlInput, setSupabaseUrlInput] = useState('')
  const [supabaseAnonKeyInput, setSupabaseAnonKeyInput] = useState('')
  const [supabaseConfigSet, setSupabaseConfigSet] = useState(false)
  const [supabaseMessage, setSupabaseMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Promo code
  const [promoCodeInput, setPromoCodeInput] = useState('')
  const [promoCodeSet, setPromoCodeSet] = useState(false)
  const [promoCodeMessage, setPromoCodeMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Redirect countdown state
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null)

  // Trailer API keys
  const [veo3KeysInput, setVeo3KeysInput] = useState('')
  const [veo3KeysSet, setVeo3KeysSet] = useState(false)
  const [veo3Message, setVeo3Message] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const [musicgptKeyInput, setMusicgptKeyInput] = useState('')
  const [musicgptKeySet, setMusicgptKeySet] = useState(false)
  const [musicgptMessage, setMusicgptMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Fal API Key
  const [_falKeySet, setFalKeySet] = useState(false)

  // Video simulator mode (returns placeholder videos instead of calling real APIs)
  const [videoSimulatorMode, setVideoSimulatorModeState] = useState(false)


  // Per-user API keys (server-managed)
  const [userApiKeys, setUserApiKeys] = useState<Array<{ id: string; key_prefix: string; key_value?: string | null; name: string; created_at: string; last_used_at: string | null }>>([])
  const [newKeyName, setNewKeyName] = useState('')
  const [apiKeysLoading, setApiKeysLoading] = useState(false)
  const [apiKeysError, setApiKeysError] = useState<string | null>(null)
  const [visibleKeyIds, setVisibleKeyIds] = useState<Set<string>>(new Set())
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null)

  useEffect(() => {
    setApiUrl(getConfiguredApiBase())
    setApiKeySet(hasGoogleApiKey())
    setPromoCodeSet(hasPromoCode())
    setVeo3KeysSet(hasVeo3ApiKeys())
    setMusicgptKeySet(hasMusicGptApiKey())
    // Supabase config
    setSupabaseUrlInput(getConfiguredSupabaseUrl())
    setSupabaseAnonKeyInput(getConfiguredSupabaseAnonKey())
    setSupabaseConfigSet(hasSupabaseConfig())
    setFalKeySet(hasFalApiKey())
    setVideoSimulatorModeState(getVideoSimulatorMode())
  }, [])

  // Load per-user API keys when logged in
  useEffect(() => {
    if (session) {
      loadApiKeys()
    }
  }, [session])

  const loadApiKeys = async () => {
    setApiKeysLoading(true)
    setApiKeysError(null)
    try {
      const keys = await api.listApiKeys()
      setUserApiKeys(keys)
    } catch {
      setApiKeysError('Failed to load API keys')
    } finally {
      setApiKeysLoading(false)
    }
  }

  const handleCreateApiKey = async () => {
    const name = newKeyName.trim()
    if (!name) return
    setApiKeysError(null)
    try {
      await api.createApiKey(name)
      setNewKeyName('')
      await loadApiKeys()
    } catch {
      setApiKeysError('Failed to create API key')
    }
  }

  const handleRevokeApiKey = async (keyId: string) => {
    setApiKeysError(null)
    try {
      await api.revokeApiKey(keyId)
      await loadApiKeys()
    } catch {
      setApiKeysError('Failed to revoke API key')
    }
  }

  const handleCopyKey = (keyId: string, keyValue: string) => {
    navigator.clipboard.writeText(keyValue)
    setCopiedKeyId(keyId)
    setTimeout(() => setCopiedKeyId(null), 2000)
  }

  const toggleKeyVisibility = (keyId: string) => {
    setVisibleKeyIds((prev) => {
      const next = new Set(prev)
      if (next.has(keyId)) {
        next.delete(keyId)
      } else {
        next.add(keyId)
      }
      return next
    })
  }

  // Countdown timer for redirect
  useEffect(() => {
    if (redirectCountdown !== null && redirectCountdown > 0) {
      const timer = setTimeout(() => {
        setRedirectCountdown(redirectCountdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    } else if (redirectCountdown === 0) {
      if (location.pathname !== '/') {
        navigate('/')
      } else {
        window.location.reload()
      }
    }
  }, [redirectCountdown, navigate, location.pathname])

  const handleSaveApiUrl = () => {
    let url = apiUrl.trim()
    // Auto-add https:// if missing protocol (Railway and production APIs require HTTPS)
    if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url
    }
    // Remove trailing slash to prevent double-slash URLs (e.g., //projects)
    url = url.replace(/\/+$/, '')
    setApiBase(url)
    onSave?.()
    // Navigate to home page to refresh with new API endpoint
    window.location.href = '/'
  }

  const handleSaveSupabaseConfig = () => {
    if (!supabaseUrlInput.trim() || !supabaseAnonKeyInput.trim()) {
      setSupabaseMessage({ type: 'error', text: 'Both URL and Anon Key are required' })
      return
    }

    let url = supabaseUrlInput.trim()
    // Auto-add https:// if missing protocol
    if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url
    }
    setSupabaseUrl(url)
    setSupabaseAnonKey(supabaseAnonKeyInput.trim())
    reinitializeSupabase()
    setSupabaseConfigSet(true)
    setSupabaseMessage({ type: 'success', text: 'Supabase config saved. Reloading...' })
    onSave?.()
    // Reload to reinitialize auth
    setTimeout(() => {
      window.location.href = '/'
    }, 1000)
  }

  const handleClearSupabaseConfig = () => {
    setSupabaseUrl('')
    setSupabaseAnonKey('')
    reinitializeSupabase()
    setSupabaseUrlInput('')
    setSupabaseAnonKeyInput('')
    setSupabaseConfigSet(false)
    setSupabaseMessage({ type: 'success', text: 'Supabase config cleared. Reloading...' })
    setTimeout(() => {
      window.location.href = '/'
    }, 1000)
  }

  const handleSaveApiKey = () => {
    if (!apiKeyInput.trim()) {
      setApiKeyMessage({ type: 'error', text: 'API key cannot be empty' })
      return
    }

    setGoogleApiKey(apiKeyInput.trim())
    setApiKeySet(true)
    setApiKeyInput('')
    setApiKeyMessage({ type: 'success', text: 'API key saved to browser' })
  }

  const handleClearApiKey = () => {
    setGoogleApiKey('')
    setApiKeySet(false)
    setApiKeyMessage({ type: 'success', text: 'API key cleared' })
  }

  // Promo code handlers
  const handleSavePromoCode = () => {
    if (!promoCodeInput.trim()) {
      setPromoCodeMessage({ type: 'error', text: 'Promo code cannot be empty' })
      return
    }

    setPromoCode(promoCodeInput.trim())
    setPromoCodeSet(true)
    setPromoCodeInput('')
    setPromoCodeMessage({ type: 'success', text: 'VIP Access Activated! Redirecting to home in 3s...' })
    setRedirectCountdown(3)
  }

  const handleClearPromoCode = () => {
    setPromoCode('')
    setPromoCodeSet(false)
    setPromoCodeMessage({ type: 'success', text: 'Promo code cleared' })
  }

  // VEO3 handlers
  const handleSaveVeo3Keys = () => {
    if (!veo3KeysInput.trim()) {
      setVeo3Message({ type: 'error', text: 'API keys cannot be empty' })
      return
    }

    setVeo3ApiKeys(veo3KeysInput.trim())
    setVeo3KeysSet(true)
    setVeo3KeysInput('')
    setVeo3Message({ type: 'success', text: 'VEO3 API keys saved to browser' })
  }

  const handleClearVeo3Keys = () => {
    setVeo3ApiKeys('')
    setVeo3KeysSet(false)
    setVeo3Message({ type: 'success', text: 'VEO3 API keys cleared' })
  }

  // MusicGPT handlers
  const handleSaveMusicGptKey = () => {
    if (!musicgptKeyInput.trim()) {
      setMusicgptMessage({ type: 'error', text: 'API key cannot be empty' })
      return
    }

    setMusicGptApiKey(musicgptKeyInput.trim())
    setMusicgptKeySet(true)
    setMusicgptKeyInput('')
    setMusicgptMessage({ type: 'success', text: 'MusicGPT API key saved to browser' })
  }

  const handleClearMusicGptKey = () => {
    setMusicGptApiKey('')
    setMusicgptKeySet(false)
    setMusicgptMessage({ type: 'success', text: 'MusicGPT API key cleared' })
  }


  const handleCopyToken = async () => {
    if (!session?.access_token) return
    try {
      await navigator.clipboard.writeText(session.access_token)
      setTokenCopied(true)
      setTimeout(() => setTokenCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy token:', err)
    }
  }

  return (
    <div className={`space-y-5 ${compact ? '' : 'p-4'}`}>
      {/* API Endpoint */}
      <div>
        <label className="block text-sm text-text-secondary mb-1">API Endpoint</label>
        <input
          type="text"
          value={apiUrl}
          onChange={(e) => setApiUrl(e.target.value)}
          placeholder="http://localhost:9000"
          className="w-full px-3 py-2 bg-surface-dark rounded border border-white/10 text-sm focus:outline-none focus:border-primary"
        />
        <p className="text-xs text-text-muted mt-1">
          Your local DreamWright API server
        </p>
        <button
          onClick={handleSaveApiUrl}
          className="mt-2 w-full px-3 py-2 bg-primary hover:bg-primary-light rounded text-sm font-medium transition-colors"
        >
          Save & Reload
        </button>
      </div>

      <div className="border-t border-white/10" />

      {/* Supabase Configuration */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-sm text-text-secondary">Authentication (Supabase)</label>
          {supabaseConfigSet && (
            <span className="text-xs text-green-400 flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Configured
            </span>
          )}
        </div>
        <input
          type="text"
          value={supabaseUrlInput}
          onChange={(e) => setSupabaseUrlInput(e.target.value)}
          placeholder="https://your-project.supabase.co"
          className="w-full px-3 py-2 bg-surface-dark rounded border border-white/10 text-sm focus:outline-none focus:border-primary mb-2"
        />
        <input
          type="password"
          value={supabaseAnonKeyInput}
          onChange={(e) => setSupabaseAnonKeyInput(e.target.value)}
          placeholder="Supabase anon key"
          className="w-full px-3 py-2 bg-surface-dark rounded border border-white/10 text-sm focus:outline-none focus:border-primary"
        />
        <p className="text-xs text-text-muted mt-1">
          Configure Supabase for authentication. Get your credentials from your{' '}
          <a
            href="https://supabase.com/dashboard"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-primary-light"
          >
            Supabase project settings
          </a>
        </p>
        {supabaseMessage && (
          <p className={`text-xs mt-1 ${supabaseMessage.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
            {supabaseMessage.text}
          </p>
        )}
        <div className="flex gap-2 mt-2">
          <button
            onClick={handleSaveSupabaseConfig}
            disabled={!supabaseUrlInput.trim() || !supabaseAnonKeyInput.trim()}
            className="flex-1 px-3 py-2 bg-primary hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed rounded text-sm font-medium transition-colors"
          >
            Save & Reload
          </button>
          {supabaseConfigSet && (
            <button
              onClick={handleClearSupabaseConfig}
              className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded text-sm transition-colors"
            >
              Clear
            </button>
          )}
        </div>

        {/* Copy JWT Token - only shown when authenticated */}
        {session?.access_token && (
          <div className="mt-4 p-3 rounded-lg border border-white/10 bg-surface-dark">
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm text-text-secondary">JWT Access Token</label>
                <p className="text-xs text-text-muted mt-1">
                  Copy for debugging API calls or testing
                </p>
              </div>
              <button
                onClick={handleCopyToken}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  tokenCopied
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-surface-2 hover:bg-border text-foreground'
                }`}
              >
                {tokenCopied ? (
                  <>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Copied!
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy Token
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Per-User API Keys - only shown when logged in */}
      {session && (
        <>
          <div className="border-t border-white/10" />

          <div>
            <h3 className="text-sm font-medium text-white/80 mb-1">API Keys</h3>
            <p className="text-xs text-white/40 mb-3">
              Generate keys to access DreamWright programmatically via the REST API.
            </p>

            {/* Create form */}
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateApiKey()}
                placeholder="Key name (e.g. my-automation)"
                className="flex-1 px-3 py-2 bg-surface-dark rounded border border-white/10 text-sm focus:outline-none focus:border-primary"
              />
              <button
                onClick={handleCreateApiKey}
                disabled={!newKeyName.trim()}
                className="px-3 py-2 bg-primary hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed rounded text-sm font-medium transition-colors whitespace-nowrap"
              >
                Generate Key
              </button>
            </div>

            {apiKeysError && (
              <p className="text-xs text-red-400 mb-2">{apiKeysError}</p>
            )}

            {/* Key list */}
            {apiKeysLoading ? (
              <p className="text-xs text-white/40">Loading keys...</p>
            ) : userApiKeys.length === 0 ? (
              <p className="text-xs text-white/40">No API keys yet.</p>
            ) : (
              <div className="space-y-2">
                {userApiKeys.map((k) => {
                  const isVisible = visibleKeyIds.has(k.id)
                  const isCopied = copiedKeyId === k.id
                  const displayKey = k.key_value
                    ? isVisible ? k.key_value : k.key_prefix + '••••••••••••••••'
                    : k.key_prefix + '...'
                  return (
                    <div
                      key={k.id}
                      className="p-2.5 rounded-lg border border-white/10 bg-surface-dark"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-white truncate">{k.name}</span>
                        <div className="flex items-center gap-1 ml-2">
                          {k.key_value && (
                            <>
                              <button
                                onClick={() => toggleKeyVisibility(k.id)}
                                className="px-2 py-1 bg-white/10 hover:bg-white/20 text-white/80 rounded text-xs transition-colors"
                                title={isVisible ? 'Hide key' : 'Show key'}
                              >
                                {isVisible ? 'Hide' : 'Show'}
                              </button>
                              <button
                                onClick={() => handleCopyKey(k.id, k.key_value!)}
                                className={`px-2 py-1 rounded text-xs transition-colors ${
                                  isCopied
                                    ? 'bg-green-500/20 text-green-400'
                                    : 'bg-white/10 hover:bg-white/20 text-white/80'
                                }`}
                              >
                                {isCopied ? 'Copied!' : 'Copy'}
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleRevokeApiKey(k.id)}
                            className="px-2 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded text-xs transition-colors whitespace-nowrap"
                          >
                            Revoke
                          </button>
                        </div>
                      </div>
                      <code className="block text-xs text-white/60 mt-1 break-all">{displayKey}</code>
                      <div className="text-xs text-white/40 mt-0.5">
                        Created {new Date(k.created_at).toLocaleDateString()}
                        {' · '}
                        {k.last_used_at
                          ? `Last used ${new Date(k.last_used_at).toLocaleDateString()}`
                          : 'Never used'}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </>
      )}

      <div className="border-t border-white/10" />

      {/* Promo Code */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-sm text-text-secondary">Promo Code</label>
          {promoCodeSet && (
            <span className="text-xs text-green-400 flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              VIP Access Active
            </span>
          )}
        </div>
        <input
          type="text"
          value={promoCodeInput}
          onChange={(e) => setPromoCodeInput(e.target.value)}
          placeholder={promoCodeSet ? "Enter new code to update" : "Enter promo code"}
          className="w-full px-3 py-2 bg-surface-dark rounded border border-white/10 text-sm focus:outline-none focus:border-primary"
        />
        <p className="text-xs text-text-muted mt-1">
          Have a promo code? Enter it to unlock all AI generation features without providing your own API keys.
        </p>
        {promoCodeMessage && (
          <p className={`text-xs mt-1 ${promoCodeMessage.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
            {redirectCountdown !== null
              ? `VIP Access Activated! Redirecting to home in ${redirectCountdown}s...`
              : promoCodeMessage.text}
          </p>
        )}
        <div className="flex gap-2 mt-2">
          <button
            onClick={handleSavePromoCode}
            disabled={!promoCodeInput.trim()}
            className="flex-1 px-3 py-2 bg-primary hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed rounded text-sm font-medium transition-colors"
          >
            Apply Code
          </button>
          {promoCodeSet && (
            <button
              onClick={handleClearPromoCode}
              className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded text-sm transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="border-t border-white/10" />

      {/* Manual API Keys Section */}
      <div>
        <h3 className="text-sm font-medium text-foreground mb-1">Manual API Keys</h3>
        <p className="text-xs text-text-muted mb-4">
          {promoCodeSet
            ? "You have VIP access. These fields are optional - only use them to override with your own keys."
            : "No promo code? Provide your own API keys to enable generation features."}
        </p>

      {/* Google API Key */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-sm text-text-secondary">Google API Key</label>
          {apiKeySet && (
            <span className="text-xs text-green-400 flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Saved in browser
            </span>
          )}
        </div>
        <input
          type="password"
          value={apiKeyInput}
          onChange={(e) => setApiKeyInput(e.target.value)}
          placeholder={apiKeySet ? "Enter new key to update" : "Enter your Google API key"}
          className="w-full px-3 py-2 bg-surface-dark rounded border border-white/10 text-sm focus:outline-none focus:border-primary"
        />
        <p className="text-xs text-text-muted mt-1">
          Required for AI generation. Get one from{' '}
          <a
            href="https://aistudio.google.com/app/apikey"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-primary-light"
          >
            Google AI Studio
          </a>
        </p>
        {apiKeyMessage && (
          <p className={`text-xs mt-1 ${apiKeyMessage.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
            {apiKeyMessage.text}
          </p>
        )}
        <div className="flex gap-2 mt-2">
          <button
            onClick={handleSaveApiKey}
            disabled={!apiKeyInput.trim()}
            className="flex-1 px-3 py-2 bg-primary hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed rounded text-sm font-medium transition-colors"
          >
            Save Key
          </button>
          {apiKeySet && (
            <button
              onClick={handleClearApiKey}
              className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded text-sm transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      </div>
      </div>

      <div className="border-t border-white/10" />

      {/* Trailer Generation Section */}
      <div>
        <h3 className="text-sm font-medium text-foreground mb-3">Trailer Generation</h3>
        <p className="text-xs text-text-muted mb-4">
          {promoCodeSet
            ? "You have VIP access. These fields are optional - only use them to override with your own keys."
            : "These keys are only needed for video trailer generation."}
        </p>

        {/* VEO3 API Keys */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm text-text-secondary">VEO3 API Keys</label>
            {veo3KeysSet && (
              <span className="text-xs text-green-400 flex items-center gap-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Saved in browser
              </span>
            )}
          </div>
          <input
            type="password"
            value={veo3KeysInput}
            onChange={(e) => setVeo3KeysInput(e.target.value)}
            placeholder={veo3KeysSet ? "Enter new keys to update" : "Enter comma-separated API keys"}
            className="w-full px-3 py-2 bg-surface-dark rounded border border-white/10 text-sm focus:outline-none focus:border-primary"
          />
          <p className="text-xs text-text-muted mt-1">
            Google API keys for Veo 3 video generation. Multiple keys (comma-separated) enable parallel segment generation.
          </p>
          {veo3Message && (
            <p className={`text-xs mt-1 ${veo3Message.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
              {veo3Message.text}
            </p>
          )}
          <div className="flex gap-2 mt-2">
            <button
              onClick={handleSaveVeo3Keys}
              disabled={!veo3KeysInput.trim()}
              className="flex-1 px-3 py-2 bg-primary hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed rounded text-sm font-medium transition-colors"
            >
              Save Keys
            </button>
            {veo3KeysSet && (
              <button
                onClick={handleClearVeo3Keys}
                className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded text-sm transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* MusicGPT API Key */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm text-text-secondary">MusicGPT API Key</label>
            {musicgptKeySet && (
              <span className="text-xs text-green-400 flex items-center gap-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Saved in browser
              </span>
            )}
          </div>
          <input
            type="password"
            value={musicgptKeyInput}
            onChange={(e) => setMusicgptKeyInput(e.target.value)}
            placeholder={musicgptKeySet ? "Enter new key to update" : "Enter your MusicGPT API key"}
            className="w-full px-3 py-2 bg-surface-dark rounded border border-white/10 text-sm focus:outline-none focus:border-primary"
          />
          <p className="text-xs text-text-muted mt-1">
            API key for MusicGPT music generation (background music for trailers). Get one from{' '}
            <a
              href="https://musicgpt.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary-light"
            >
              MusicGPT
            </a>
          </p>
          {musicgptMessage && (
            <p className={`text-xs mt-1 ${musicgptMessage.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
              {musicgptMessage.text}
            </p>
          )}
          <div className="flex gap-2 mt-2">
            <button
              onClick={handleSaveMusicGptKey}
              disabled={!musicgptKeyInput.trim()}
              className="flex-1 px-3 py-2 bg-primary hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed rounded text-sm font-medium transition-colors"
            >
              Save Key
            </button>
            {musicgptKeySet && (
              <button
                onClick={handleClearMusicGptKey}
                className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded text-sm transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-white/10" />

      {/* Short Drama Video Section */}
      <div>
        <h3 className="text-sm font-medium text-foreground mb-3">Short Drama Video</h3>
        <p className="text-xs text-text-muted mb-4">
          Choose which AI model to use for generating short drama video clips from webtoon panels.
        </p>

        {/* Video Simulator Mode Toggle */}
        <div className="mb-4 p-3 rounded-lg border border-purple-500/30 bg-purple-500/10">
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-foreground">Video Simulator Mode</label>
              <p className="text-xs text-text-secondary mt-1">
                Return placeholder videos instead of calling real APIs. Useful for testing without API keys.
              </p>
            </div>
            <button
              onClick={() => {
                const newValue = !videoSimulatorMode
                setVideoSimulatorMode(newValue)
                setVideoSimulatorModeState(newValue)
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                videoSimulatorMode ? 'bg-purple-500' : 'bg-border'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  videoSimulatorMode ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          {videoSimulatorMode && (
            <div className="mt-2 flex items-center gap-2 text-xs text-purple-300">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Videos will be placeholders. No API keys required.
            </div>
          )}
        </div>

        {/* Video model is now selected per-node on VideoGeneratorNode */}
      </div>

      <div className="border-t border-white/10" />

      {/* Experimental Features */}
      <div>
        <h3 className="text-sm font-medium text-foreground mb-3">Experimental Features</h3>
        <p className="text-xs text-text-muted mb-4">
          These features are under development and may not work as expected.
        </p>

      </div>
    </div>
  )
}
